<script setup>
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { toast } from "vue-sonner";
  import { RotateCcw } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Spinner } from "@/components/ui/spinner";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useCAdsetsStore } from "@stores/cAdsets";
  import { useCCampaignsStore } from "@stores/cCampaigns";

  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign, hasMCampaignChanged } = storeToRefs(mCampaignStore);
  const { fetchMCampaign, runBidder, saveMCampaign } = mCampaignStore;

  const cAdsetsStore = useCAdsetsStore();
  const { cAdsets } = storeToRefs(cAdsetsStore);
  const { fetchCAdsets, saveCAdsetsBudgets } = cAdsetsStore;

  const cCampaignsStore = useCCampaignsStore();
  const { fetchCCampaigns, saveCCampaignsBudgets } = cCampaignsStore;

  const isRunning = ref(false);

  const mCampaignIsInactive = computed(() => currentMCampaign.value?.status === "inactive");

  const applyRecos = () => {
    cAdsets.value.forEach((cAdset) => {
      cAdset.daily_budget = cAdset.bidder_suggestion;
    });
    toast.success("Recos applied");
  };

  const handleRunBidder = async () => {
    const token = currentMCampaign.value.token;
    isRunning.value = true;
    try {
      await saveCCampaignsBudgets(token);
      await saveCAdsetsBudgets(token);
      if (hasMCampaignChanged.value) await saveMCampaign();
      await runBidder();
      await Promise.all([fetchMCampaign(token), fetchCCampaigns(token), fetchCAdsets(token)]);
      toast.success("Bidder run completed");
    } catch (err) {
      toast.error(`Bidder run failed: ${err?.message ?? "unknown error"}`);
    } finally {
      isRunning.value = false;
    }
  };
</script>

<template>
  <div class="flex items-center justify-end gap-2">
    <Button
      variant="outline"
      :disabled="isRunning"
      @click="applyRecos">
      <RotateCcw class="h-4 w-4" />
      Apply recos
    </Button>
    <Tooltip
      :disabled="!mCampaignIsInactive"
      content="This campaign is inactive, you can't run bidder.">
      <Button
        :disabled="mCampaignIsInactive || isRunning"
        @click="handleRunBidder">
        <Spinner
          v-if="isRunning"
          class="h-4 w-4" />
        <AiStar
          v-else
          class="h-4 w-4" />
        {{ isRunning ? "Running…" : "Run bidder" }}
      </Button>
    </Tooltip>
  </div>
</template>
